import { useEffect } from 'react';

export type ExportSignalStatus = 'loading' | 'ready' | 'missing' | 'failed';

interface UseExportSignalArgs {
  target: string;
  status: ExportSignalStatus;
  reason?: string;
  enabled?: boolean;
}

const READY_ATTR = 'data-export-ready';
const MISSING_ATTR = 'data-export-missing';
const FAILED_ATTR = 'data-export-failed';
const ERROR_ATTR = 'data-export-error';

const bodyStatusAttr = (target: string) => `data-export-status-${target.toLowerCase()}`;
const bodyErrorAttr = (target: string) => `data-export-error-${target.toLowerCase()}`;

const clearTargetSignals = (el: HTMLElement) => {
  el.removeAttribute(READY_ATTR);
  el.removeAttribute(MISSING_ATTR);
  el.removeAttribute(FAILED_ATTR);
  el.removeAttribute(ERROR_ATTR);
};

const clearBodyStatus = (target: string) => {
  const { body } = document;
  if (!body) {
    return;
  }
  body.removeAttribute(bodyStatusAttr(target));
  body.removeAttribute(bodyErrorAttr(target));
};

/**
 * Terminal-signal contract used by the v2 PDF export pipeline.
 * Each exportable page calls this hook with its current load state so the
 * orchestrator can react event-driven, without hardcoded timeouts.
 *
 * Status is mirrored in two places so the orchestrator can read it both before
 * and after the target DOM element mounts:
 *  - `document.body` gets `data-export-status-<lowercased-target>="loading|ready|missing|failed"`
 *    so the orchestrator can tell a page is still loading even while the
 *    `[data-ref]` node is absent (e.g. data still in flight and the owning
 *    subtree hasn't rendered yet).
 *  - The `[data-ref="<target>"]` element itself still carries the strict
 *    `data-export-ready|missing|failed` attribute once it exists.
 */
export const useExportSignal = ({ target, status, reason, enabled = true }: UseExportSignalArgs) => {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const { body } = document;
    if (body) {
      body.setAttribute(bodyStatusAttr(target), status);
      if (status === 'failed' && reason) {
        body.setAttribute(bodyErrorAttr(target), reason);
      } else {
        body.removeAttribute(bodyErrorAttr(target));
      }
    }

    const el = document.querySelector<HTMLElement>(`[data-ref="${target}"]`);
    if (el) {
      clearTargetSignals(el);
      switch (status) {
        case 'ready':
          el.setAttribute(READY_ATTR, 'true');
          break;
        case 'missing':
          el.setAttribute(MISSING_ATTR, 'true');
          break;
        case 'failed':
          el.setAttribute(FAILED_ATTR, 'true');
          if (reason) {
            el.setAttribute(ERROR_ATTR, reason);
          }
          break;
        case 'loading':
        default:
          // Attributes already cleared above — leave target in the "not yet ready" state.
          break;
      }
    }

    return () => {
      clearBodyStatus(target);
      const currentEl = document.querySelector<HTMLElement>(`[data-ref="${target}"]`);
      if (currentEl) {
        clearTargetSignals(currentEl);
      }
    };
  }, [target, status, reason, enabled]);
};
